/*!
 * @license
 * Alfresco Example Content Application
 *
 * Copyright (C) 2005 - 2020 Alfresco Software Limited
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail.  Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with Alfresco. If not, see <http://www.gnu.org/licenses/>.
 */

import { ProfileState, RuleContext } from '@alfresco/adf-extensions';
import { GROUP_ARCHIVERO, GROUP_LOANS_SITE_COLLABORATOR, GROUP_LOANS_SITE_CONTRIBUTOR, GROUP_LOANS_SITE_MANAGER, RENEWAL_ASPECT, UDEU_LOAN_BORROWED_STATUS, UDEU_LOAN_EXPIRED_STATUS, UDEU_LOAN_IN_PROGRESS_STATUS, UDEU_LOAN_REQUESTED_STATUS, UDEU_LOANS_TYPE, UDEU_STATUS_PROP } from '../model/contants/deusto';
import { NodeEntry, Person } from '@alfresco/js-api';
import { hasAspect, hasPropertyValue, isAllowedOperation, isInGroup, isType, isTypeViewer } from 'projects/alden-extension/src/lib/rules/alden.rules';
import { DELETE_ALLOWABLE_OPERATION, UPDATE_ALLOWABLE_OPERATION } from 'projects/alden-extension/src/lib/models/alden-constants';
import { isMyElement } from './udeu.rules';

export function isLoansViewer(context: RuleContext): boolean {
  return isTypeViewer(context, 'loans');
}

export function canCreateLoan(context: RuleContext): boolean {
  const profile = context.profile;
  return isInGroup(profile, GROUP_LOANS_SITE_MANAGER) || isInGroup(profile, GROUP_LOANS_SITE_COLLABORATOR) || isInGroup(profile, GROUP_LOANS_SITE_CONTRIBUTOR);
}

export function canViewLoan(context: RuleContext): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  if(selection.count === 1 && isType(nodes[0], UDEU_LOANS_TYPE)) {
    return true;
  }
  return false; 
}

export function canManageLoan(profile: ProfileState): boolean {
  return isInGroup(profile, GROUP_LOANS_SITE_MANAGER);
}

export function canEditLoan(context: RuleContext): boolean {
  const selection = context.selection;
  const profile = context.profile;
  const nodes = selection.nodes;
  if(selection.count === 1 && loanAllowedOperation(nodes[0], UPDATE_ALLOWABLE_OPERATION) 
    && isInGroup(profile, GROUP_LOANS_SITE_MANAGER) 
    && (hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_LOAN_IN_PROGRESS_STATUS) 
    || hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_LOAN_BORROWED_STATUS) 
    || hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_LOAN_EXPIRED_STATUS))) {
    return true;
  }
  return false; 
}

export function canValidateLoan(context: RuleContext): boolean {
  const selection = context.selection;
  const profile = context.profile;
  const nodes = selection.nodes;
  if(selection.count === 1 && loanAllowedOperation(nodes[0], UPDATE_ALLOWABLE_OPERATION) 
    && isInGroup(profile, GROUP_LOANS_SITE_MANAGER) && hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_LOAN_REQUESTED_STATUS)) {
    return true;
  }
  return false; 
}

function canAssign(context: RuleContext) {
  const selection = context.selection;
  const nodes = selection.nodes;
  if(selection.count === 1 && canAssignRegisterToLoan(context) && loanAllowedOperation(nodes[0], UPDATE_ALLOWABLE_OPERATION) 
    && hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_LOAN_IN_PROGRESS_STATUS)) {
    return true;
  }
  return false;
}

export function canAssignRegister(context: RuleContext): boolean {
  return canAssign(context);
}

export function canGrantLoan(context: RuleContext): boolean {
  return canAssign(context);
}

export function canAssignRegisterToLoan(context: RuleContext): boolean {
  const selection = context.selection;
  const profile = context.profile;
  if(selection.count > 0 && isInGroup(profile, GROUP_LOANS_SITE_MANAGER)) {
    return true;
  }
  return false; 
}

export function canReturnLoan(context: RuleContext): boolean {
  const selection = context.selection;
  const profile = context.profile;
  const nodes = selection.nodes;
  if(selection.count === 1 && loanAllowedOperation(nodes[0], UPDATE_ALLOWABLE_OPERATION) 
    && isInGroup(profile, GROUP_LOANS_SITE_MANAGER) 
    && (hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_LOAN_BORROWED_STATUS) || hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_LOAN_EXPIRED_STATUS))) {
    return true;
  }
  return false; 
}

export function canRenewLoan(context: RuleContext, personDetails: Person): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  if(selection.count === 1 && isType(nodes[0], UDEU_LOANS_TYPE) && (isMyElement(nodes[0], personDetails) || isInGroup(context.profile, GROUP_ARCHIVERO))
    && (hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_LOAN_BORROWED_STATUS) || hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_LOAN_EXPIRED_STATUS))) {
    return true;
  }
  return false; 
}

export function canValidateRenewLoan(context: RuleContext): boolean {
  const selection = context.selection;
  const profile = context.profile;
  const nodes = selection.nodes;
  if(selection.count === 1 && loanAllowedOperation(nodes[0], UPDATE_ALLOWABLE_OPERATION) 
    && isInGroup(profile, GROUP_LOANS_SITE_MANAGER) && hasAspect(nodes[0], RENEWAL_ASPECT)) {
    return true;
  }
  return false; 
}

export function canDeleteLoan(context: RuleContext): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  const profile = context.profile;
  if(selection.count > 0 && nodes.every((node) => loanAllowedOperation(node, DELETE_ALLOWABLE_OPERATION))
      && isInGroup(profile, GROUP_LOANS_SITE_MANAGER) && !nodes.some((node) => hasPropertyValue(node, UDEU_STATUS_PROP, UDEU_LOAN_BORROWED_STATUS))) {
    return true;
  }
  return false; 
}

export function canFinishLoan(context: RuleContext): boolean {
  const selection = context.selection;
  return selection.count === 1 
      && isInGroup(context.profile, GROUP_LOANS_SITE_MANAGER) 
      && loanAllowedOperation(selection.nodes[0], UPDATE_ALLOWABLE_OPERATION)
      && (hasPropertyValue(selection.nodes[0], UDEU_STATUS_PROP, UDEU_LOAN_IN_PROGRESS_STATUS) || hasPropertyValue(selection.nodes[0], UDEU_STATUS_PROP, UDEU_LOAN_EXPIRED_STATUS));
}

function loanAllowedOperation(node: NodeEntry, allowableOperation: string): boolean {
  return isType(node, UDEU_LOANS_TYPE) && isAllowedOperation(node, allowableOperation);
}